import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import Pagination from "../../Common/Pagination/Pagination";
import { ArchivedMembersAction } from "../../../Redux/action/ArchivedMembersAction";
import { Link, useLocation } from "react-router-dom";
import { TbExternalLink } from "react-icons/tb";
import { ArchiveRounded, PersonOffRounded } from "@mui/icons-material";

const ArchivedMembers = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const [search, setSearch] = useState("");
  const PRIMARY_COLOR = "#2d6d96";

  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const vId = queryParams.get("vendorId");

  const dispatch = useDispatch();
  const { userResult } = useSelector((state) => state.Auth);
  const vendorId = vId || userResult?.vendorid;

  const userInfo = useSelector(
    (state) => state.ArchivedMembers?.result?.content,
  );
  const totalPages = useSelector(
    (state) => state.ArchivedMembers?.result?.totalPages,
  );
  const isLoading = useSelector((state) => state.ArchivedMembers?.isLoading);

  useEffect(() => {
    dispatch(
      ArchivedMembersAction({
        vendorId,
        search,
        pageNumber,
      }),
    );
  }, [dispatch, vendorId, pageNumber, search]);

  const handlePageChange = (page) => setPageNumber(page);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          background: "#ffffff",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            width: { xs: "100%", sm: "auto" },
            mb: { xs: 1, sm: 0 },
          }}
        >
          <Avatar
            sx={{
              bgcolor: "#F1F5F9",
              color: PRIMARY_COLOR,
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
            }}
          >
            <ArchiveRounded fontSize="small" />
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "16px", sm: "20px" },
                color: "#1E293B",
                letterSpacing: "-0.4px",
                lineHeight: 1.2,
              }}
            >
              Archived
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#64748B",
                fontWeight: 500,
                display: "block",
                marginTop: "2px",
                fontSize: { xs: "11px", sm: "13px" },
              }}
            >
              Historical records of deactivated team members
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "13px" },
            fontWeight: 700,
            color: PRIMARY_COLOR,
            padding: { xs: "3px 8px", sm: "4px 10px" },
            borderRadius: "999px",
            backgroundColor: `${PRIMARY_COLOR}14`,
            boxShadow: `0 2px 6px ${PRIMARY_COLOR}33`,
            whiteSpace: "nowrap",
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
        >
          Archived: {userInfo?.length || 0}
        </Typography>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          overflowX: { xs: "auto", sm: "hidden" },
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
        }}
      >
        <Table sx={{ minWidth: { xs: 600, sm: 900 } }}>
          <TableHead>
            <TableRow sx={{ background: "#F8FAFC", whiteSpace: "nowrap" }}>
              <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>
                Name & ID
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                Designation
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                Contact Info
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                Location
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475569" }}>
                Reason
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, color: "#475569" }}
                align="right"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                  <CircularProgress size={35} sx={{ color: PRIMARY_COLOR }} />
                </TableCell>
              </TableRow>
            ) : userInfo?.length > 0 ? (
              userInfo.map((item, i) => (
                <TableRow
                  key={i}
                  hover
                  sx={{
                    transition: "0.2s",
                    "&:hover": {
                      background: "#F8FAFC",
                      "& td:first-of-type": {
                        borderLeft: `4px solid ${PRIMARY_COLOR}`,
                      },
                    },
                    "& td": {
                      borderLeft: "4px solid transparent",
                      transition: "0.2s",
                    }
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={item?.profileImageUrl}
                        sx={{
                          width: 42,
                          height: 42,
                          bgcolor: "#F1F5F9",
                          color: PRIMARY_COLOR,
                          fontWeight: 700,
                          fontSize: "14px",
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        {item?.candidateName
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#1E293B",
                            fontSize: "0.95rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.candidateName || "N/A"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: PRIMARY_COLOR,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ID: {item?.candidateEmpId}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ color: "#475569", fontWeight: 500 }}>
                    {item?.candidateDesignation || "—"}
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "#1E293B", fontWeight: 500 }}
                    >
                      {item?.candidateEmail || "—"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      {item?.candidateMobile || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                    {item?.candidateAddress || "Remote"}
                  </TableCell>

                  <TableCell>
                    <Tooltip title={item?.reason || "No reason provided"} arrow>
                      <Box
                        sx={{
                          bgcolor: "#F1F5F9",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          display: "inline-block",
                          maxWidth: 180,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: "0.8rem",
                            color: "#475569",
                            fontWeight: 600,
                          }}
                        >
                          {item?.reason || "N/A"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="View Candidate Log">
                      <IconButton
                        component={Link}
                        to={`/candidate-profile-view/${item?.candidateId}`}
                        sx={{
                          color: PRIMARY_COLOR,
                          bgcolor: "#F0F7FF",
                          "&:hover": { bgcolor: "#E0F2FE" },
                        }}
                        size="small"
                      >
                        <TbExternalLink size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                  <Stack alignItems="center" spacing={1} sx={{ opacity: 0.5 }}>
                    <PersonOffRounded sx={{ fontSize: 48 }} />
                    <Typography sx={{ fontWeight: 600, color: "#64748B" }}>
                      No archived records found
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION SECTION */}
      {!isLoading && userInfo?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, pb: 2 }}>
          <Pagination
            totalPages={totalPages}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default ArchivedMembers;
